import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useDispatch } from "react-redux";
import { RotateCcw, Search, UserSearch } from "lucide-react";
import { getExamListNew, getRegisteredUserData } from "../../Action/courseAction";
import { checkValidName } from "../../Utils/validators";
import {
  CustomAdminTable,
  CustomBadge,
  CustomButton,
  CustomDropdowns,
  CustomEmptyState,
  CustomInputField,
  CustomPageLayout,
} from "../../Component/customComponents";
import { BatchNames, useCascadingLists } from "../Common/transactionCommon";

// Card look for page sections (Bootstrap utilities — no page CSS file)
const CARD = "bg-white border rounded-3 shadow-sm";

const EMPTY_FORM = { firstName: "", lastName: "", exam: "", subExam: "", examStream: "" };

const NAME_RULE = "Use letters only (one space allowed between two words)";

/** Letters, optionally "First Last" — checked on the trimmed value so a space can be typed */
const nameError = (value) => (value.trim() && !checkValidName(value.trim()) ? NAME_RULE : "");

/** Round photo, or initials when there's no photo */
const Avatar = ({ src, firstName, lastName }) => {
  const [broken, setBroken] = useState(false);
  if (src && !broken) {
    return (
      <img
        src={src}
        alt=""
        width={40}
        height={40}
        className="rounded-circle border"
        style={{ objectFit: "cover" }}
        onError={() => setBroken(true)}
      />
    );
  }
  const initials = `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase() || "?";
  return (
    <span
      className="rounded-circle d-inline-flex align-items-center justify-content-center fw-bold text-white"
      style={{ width: 40, height: 40, background: "var(--cui-primary)", fontSize: 13 }}
      aria-hidden="true"
    >
      {initials}
    </span>
  );
};

/** Course list; scrolls inside the cell when long (old page: > 6 courses) */
const UserDataSearch = () => {
  const dispatch = useDispatch();

  const [form, setForm] = useState(EMPTY_FORM);
  const [examList, setExamList] = useState([]);
  const cascade = useCascadingLists({ withBatches: false });

  const [userList, setUserList] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    dispatch(getExamListNew((res) => setExamList(res || [])));
  }, []);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  /* ---------- Exam → Sub Exam → Stream (shared loader) ---------- */
  const onChangeExam = (value) => {
    setForm((prev) => ({ ...prev, exam: value, subExam: "", examStream: "" }));
    cascade.onFilterChange("exam", value, { exam: value });
  };

  const onChangeSubExam = (value) => {
    setForm((prev) => ({ ...prev, subExam: value, examStream: "" }));
    cascade.onFilterChange("subExam", value, { exam: form.exam, subExam: value });
  };

  /* ---------- validation: red border + message ---------- */
  const errors = {
    firstName:
      nameError(form.firstName) ||
      (submitted && !form.firstName.trim() ? "First name is required" : ""),
    lastName: nameError(form.lastName),
  };
  const isInvalid = Boolean(errors.firstName || errors.lastName || !form.firstName.trim());

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    if (isInvalid) return;

    const payload = { firstName: form.firstName.trim() };
    if (form.lastName.trim()) payload.lastName = form.lastName.trim();
    if (form.exam?._id) payload.examTypeId = form.exam._id;
    if (form.examStream?._id) payload.baseCourseId = form.examStream._id;

    setLoading(true);
    dispatch(
      getRegisteredUserData(
        payload,
        (data) => {
          setUserList(Array.isArray(data) ? data : []);
          setHasSearched(true);
          setSearchText("");
          setPage(1);
          setLoading(false);
        },
        () => setLoading(false),
      ),
    );
  };

  const handleReset = () => {
    setForm(EMPTY_FORM);
    setSubmitted(false);
    cascade.onFilterChange("exam", "", { exam: "" });
    setUserList([]);
    setHasSearched(false);
  };

  /* ---------- results table (search inside results + paging) ---------- */
  const filteredRows = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    if (!term) return userList;
    return userList.filter((u) =>
      [u?.firstName, u?.lastName, u?.mobileNo, u?.email, u?.exam, u?.examStream]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term)),
    );
  }, [userList, searchText]);

  const pagedRows = useMemo(
    () => filteredRows.slice((page - 1) * pageSize, page * pageSize),
    [filteredRows, page, pageSize],
  );

  const columns = [
    {
      key: "profilePicture",
      header: "Profile",
      width: 72,
      align: "center",
      render: (v, row) => <Avatar src={v} firstName={row?.firstName} lastName={row?.lastName} />,
    },
    { key: "firstName", header: "First Name", minWidth: 120 },
    { key: "lastName", header: "Last Name", minWidth: 120 },
    { key: "exam", header: "Exam Name", minWidth: 120 },
    { key: "examStream", header: "Stream Name", minWidth: 140, render: (v) => <BatchNames names={v} /> },
    { key: "mobileNo", header: "Mobile Number", minWidth: 130 },
    {
      key: "dob",
      header: "Date Of Birth",
      minWidth: 120,
      render: (v) => (v ? moment.utc(v).format("DD/MM/YYYY") : "—"),
    },
    {
      key: "userSubscriptions",
      header: "Course Name",
      wrap: true,
      minWidth: 260,
      render: (v) => <BatchNames names={(v || []).map((s) => s?.course)} />,
    },
    {
      key: "gender",
      header: "Gender",
      minWidth: 90,
      render: (v) => (v ? <CustomBadge tone="neutral" size="sm">{v}</CustomBadge> : "—"),
    },
    { key: "email", header: "Email", minWidth: 200, wrap: true },
  ];

  return (
    <CustomPageLayout title="USER DATA SEARCH">
      <div className="d-flex flex-column gap-3">
        {/* ---------- search form ---------- */}
        <form className={`${CARD} p-3`} onSubmit={handleSubmit} noValidate>
          <div className="row g-3">
            <CustomInputField
              colClass="col-lg-3 col-md-6 col-12"
              label="First Name"
              isRequired
              placeholder="Enter first name"
              value={form.firstName}
              maxLength={15}
              onChange={(v) => setField("firstName", v)}
              error={errors.firstName}
              noMargin
            />
            <CustomInputField
              colClass="col-lg-3 col-md-6 col-12"
              label="Last Name"
              placeholder="Enter last name"
              value={form.lastName}
              maxLength={15}
              onChange={(v) => setField("lastName", v)}
              error={errors.lastName}
              noMargin
            />
            <CustomDropdowns
              colClass="col-lg-3 col-md-6 col-12"
              label="Exam Name"
              options={examList}
              value={form.exam}
              onChange={onChangeExam}
            />
            {form.exam && cascade.subExamList.length > 0 && (
              <CustomDropdowns
                colClass="col-lg-3 col-md-6 col-12"
                label="Sub Exam Name"
                options={cascade.subExamList}
                value={form.subExam}
                onChange={onChangeSubExam}
              />
            )}
            <CustomDropdowns
              colClass="col-lg-3 col-md-6 col-12"
              label="Exam Stream"
              options={cascade.examStreamList}
              value={form.examStream}
              onChange={(v) => setField("examStream", v)}
              disabled={!form.exam}
              hint={!form.exam ? "Select an exam first" : undefined}
            />
            {/* pt-lg-4 lines the buttons up with the inputs below their labels */}
            <div className="col d-flex flex-wrap gap-2 align-items-start justify-content-end pt-lg-4">
              <CustomButton variant="ghost" icon={<RotateCcw size={15} />} onClick={handleReset}>
                Reset
              </CustomButton>
              <CustomButton type="submit" icon={<Search size={15} />} loading={loading}>
                Search
              </CustomButton>
            </div>
          </div>
        </form>

        {/* ---------- results ---------- */}
        {hasSearched ? (
          <CustomAdminTable
            id="user-data-search-table"
            title={`Results (${userList.length})`}
            columns={columns}
            data={pagedRows}
            loading={loading}
            rowKey={(row, index) => row?._id || row?.mobileNo || index}
            enableSerialNumber
            serialHeader="S.No"
            search={{
              value: searchText,
              onChange: (v) => {
                setSearchText(v);
                setPage(1);
              },
              placeholder: "Search in results...",
            }}
            pagination={{
              page,
              pageSize,
              total: filteredRows.length,
              onPageChange: setPage,
              onPageSizeChange: (size) => {
                setPageSize(size);
                setPage(1);
              },
            }}
            emptyTitle={searchText ? "No matching users" : "No users found"}
            emptyDescription={
              searchText ? "Try a different search." : "Try a different name or remove the exam filters."
            }
            maxHeight="70vh"
          />
        ) : (
          <div className={CARD}>
            <CustomEmptyState
              icon={<UserSearch size={26} />}
              title="Search for a student"
              description="Enter at least a first name, then click Search."
            />
          </div>
        )}
      </div>
    </CustomPageLayout>
  );
};

export default UserDataSearch;
